import { getAdobeAccessToken } from '../auth/adobeIms';
import { actionUrl } from './database';

// ============================================================================
// Types — Cleanup data (manual overrides of the requests report rows)
// ============================================================================

export type CleanupFieldType = 'text' | 'number' | 'date' | 'enum';

/** Editable column definition, derived server-side from the report config's fieldMap. */
export interface CleanupFieldSpec {
  name: string;
  type: CleanupFieldType;
  format?: string;       // date fields, e.g. 'dd/MM/yy'
  options?: string[];    // enum fields (value-map outputs)
  readOnly?: boolean;    // requestID (the join key)
  derived?: boolean;     // fy/q from an adobe-fy-quarter rule: created_fy / created_q (from entry), close_fy_calc / close_q_calc (from completion); recomputed unless overridden
}

export type CleanupValue = string | number | null;
export type CleanupRecord = Record<string, CleanupValue>;
export type CleanupSource = 'synced' | 'uploaded';

export interface CleanupRow {
  requestID: string;
  source: CleanupSource;
  merged: CleanupRecord;          // effective values (base + manual overrides)
  base: CleanupRecord;            // underlying synced/uploaded values
  overriddenFields: string[];
  differsFields: string[];        // overridden fields whose base value changed since the override
  updatedBy: string | null;
  updatedAt: string | null;
}

export interface CleanupListParams {
  offset?: number;
  limit?: number;
  search?: string;
  overriddenOnly?: boolean;
  source?: CleanupSource | 'all';
}

export interface CleanupListResponse {
  rows: CleanupRow[];
  fields: CleanupFieldSpec[];
  count: number;
  totalCount: number;
  overriddenCount: number;
  offset: number;
  limit: number;
  lastSyncedAt: string | null;
}

export interface CleanupActor {
  email: string;
  displayName: string | null;
}

// ============================================================================
// Validation — mirrors the server (actions/requestOverrides.js); the server is
// authoritative, this gives instant feedback before a save round-trip.
// ============================================================================

export type CleanupValidation = { ok: true; value: CleanupValue } | { ok: false; error: string };

const DATE_TOKEN_RE = /yyyy|yy|MM|dd/g;

function parseDateParts(value: string, format: string): { y: number; m: number; d: number } | null {
  const tokens = format.match(DATE_TOKEN_RE) ?? [];
  const escaped = format.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = value.match(new RegExp('^' + escaped.replace(DATE_TOKEN_RE, '(\\d+)') + '$'));
  if (!match) return null;
  const parts: Record<string, number> = {};
  tokens.forEach((t, i) => {
    parts[t] = parseInt(match[i + 1], 10);
  });
  const y = parts.yyyy ?? (parts.yy !== undefined ? (parts.yy < 50 ? 2000 + parts.yy : 1900 + parts.yy) : NaN);
  if (!Number.isFinite(y)) return null;
  const m = parts.MM ?? 1;
  const d = parts.dd ?? 1;
  const date = new Date(y, m - 1, d);
  // Round-trip check rejects impossible dates such as 31/02/26.
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
  return { y, m, d };
}

export function isValidDateString(value: string, format: string): boolean {
  return parseDateParts(value, format) !== null;
}

/** Sortable number (yyyymmdd) for a formatted date string; 0 when blank/unparseable. */
export function dateSortKey(value: CleanupValue, format: string | undefined): number {
  if (value === null || value === undefined || !format) return 0;
  const p = parseDateParts(String(value), format);
  return p ? p.y * 10000 + p.m * 100 + p.d : 0;
}

export function validateCleanupInput(
  spec: CleanupFieldSpec,
  raw: string,
  currentValue: CleanupValue
): CleanupValidation {
  if (spec.readOnly) return { ok: false, error: `${spec.name} is read-only` };
  const str = raw.trim();
  if (str === '') return { ok: true, value: null };

  if (spec.type === 'number') {
    const n = Number(str);
    if (!Number.isFinite(n)) return { ok: false, error: `${spec.name} must be a number` };
    return { ok: true, value: n };
  }
  if (spec.type === 'date') {
    if (!spec.format || !isValidDateString(str, spec.format)) {
      return { ok: false, error: `${spec.name} must be a valid date (${spec.format ?? 'date'})` };
    }
    return { ok: true, value: str };
  }
  if (spec.type === 'enum') {
    const allowed =
      (spec.options ?? []).includes(str) ||
      (currentValue !== null && currentValue !== undefined && String(currentValue) === str);
    if (!allowed) return { ok: false, error: `${spec.name} must be one of: ${(spec.options ?? []).join(', ')}` };
    return { ok: true, value: str };
  }
  return { ok: true, value: str };
}

/** Text shown in an input / table cell for a stored value. */
export function cleanupValueToText(value: CleanupValue | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}

export function cleanupValuesEqual(a: CleanupValue | undefined, b: CleanupValue | undefined): boolean {
  return (a ?? null) === (b ?? null);
}

// ============================================================================
// API actions
// ============================================================================

function authHeaders(token: string): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function post<T>(action: string, body: Record<string, unknown>): Promise<T> {
  const token = getAdobeAccessToken();
  const resp = await fetch(actionUrl(action), {
    method: 'POST',
    headers: authHeaders(token || ''),
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: 'unknown' }));
    throw new Error((err as { error?: string }).error || `HTTP ${resp.status}`);
  }
  return (await resp.json()) as T;
}

export function listRequestCleanup(params: CleanupListParams = {}): Promise<CleanupListResponse> {
  return post<CleanupListResponse>('db-list-request-cleanup', {
    offset: params.offset ?? 0,
    limit: params.limit ?? 50,
    search: params.search || undefined,
    overriddenOnly: params.overriddenOnly || undefined,
    source: params.source && params.source !== 'all' ? params.source : undefined,
  });
}

export async function saveRequestOverrides(
  requestId: string,
  changes: Record<string, CleanupValue>,
  actor?: CleanupActor | null
): Promise<CleanupRow> {
  const data = await post<{ row: CleanupRow }>('db-save-request-overrides', {
    requestId,
    changes,
    actor: actor ?? undefined,
  });
  return data.row;
}

export async function revertRequestOverrides(
  requestId: string,
  fields?: string[],
  actor?: CleanupActor | null
): Promise<CleanupRow> {
  const data = await post<{ row: CleanupRow }>('db-revert-request-overrides', {
    requestId,
    fields,
    actor: actor ?? undefined,
  });
  return data.row;
}
